import React, { useEffect, useState, useRef, lazy, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { RestaurantHeader } from '../components/restaurant/RestaurantHeader';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { MenuParentTabs } from '../components/restaurant/MenuParentTabs';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { FloatingCartButton } from '../components/restaurant/FloatingCartButton';
import { useRestaurantBySlug, useMenuData, useMarketplaceData } from '../lib/hooks';
import { useLanguage } from '../lib/i18n';
import { useCart } from '../lib/cart';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, Plus, ArrowDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuItem, CartItemOption, MenuCategory } from '../lib/types';
import { formatCurrency } from '../lib/utils';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useOpenOrderCheck } from '../lib/open-order-check';
import { OpenOrderAlert } from '../components/restaurant/OpenOrderAlert';
import { useTranslatedMenu } from '../lib/useTranslatedMenu';
import { translateCategory } from '../lib/translate';
// ─── Category Mapping Config ────────────────────────────────────────────────
// Kabú-specific parent grouping
const KABU_PARENT_CATEGORIES = [{
  key: 'comida',
  labelKey: 'cat_food',
  icon: '🍽️',
  subcategories: ['despertares del valle', 'sabores del mediodía', 'ensaladas', 'platos con memoria', 'de la plancha al corazón']
}, {
  key: 'bebidas',
  labelKey: 'cat_drinks',
  icon: '🥤',
  subcategories: ['bebidas calientes a base de café', 'bebidas calientes', 'bebidas frías a base de café', 'bebidas frías', 'shakes y smoothies', 'funcionales', 'bubble tea', 'cervezas', 'vinos', 'cócteles', 'mócteles', 'alcohol']
}, {
  key: 'postres',
  labelKey: 'cat_desserts',
  icon: '🍰',
  subcategories: ['postres']
}, {
  key: 'ninos',
  labelKey: 'cat_kids',
  icon: '👶',
  subcategories: ['para los pequeños']
}];
function getKabuParentKey(categoryName: string): string {
  const nameLower = categoryName.toLowerCase().trim();
  for (const parent of KABU_PARENT_CATEGORIES) {
    if (parent.subcategories.includes(nameLower)) {
      return parent.key;
    }
  }
  return 'comida';
}
// Generic emoji mapping for any category name
function getCategoryIcon(name: string): string {
  const lower = name.toLowerCase().trim();
  if (lower.includes('tacita')) return 'https://cdn.magicpatterns.com/uploads/nvDy4MrojnMpunka4CSaPN/Screenshot_2026-04-26_at_11.46.35_PM.png';
  if (lower.includes('pizza')) return '🍕';
  if (lower.includes('hamburguesa') || lower.includes('burger')) return '🍔';
  if (lower.includes('ensalada') || lower.includes('salad')) return '🥗';
  if (lower.includes('bebida') || lower.includes('drink') || lower.includes('refresco') || lower.includes('milkshake')) return '🥤';
  if (lower.includes('postre') || lower.includes('dessert') || lower.includes('copa')) return '🍨';
  if (lower.includes('favorito') || lower.includes('popular')) return '🔥';
  if (lower.includes('antojo') || lower.includes('dulce')) return '🍫';
  if (lower.includes('refrescante') || lower.includes('granizado')) return '🍧';
  if (lower.includes('fresca') || lower.includes('saludable') || lower.includes('fruta')) return '🍓';
  if (lower.includes('salado') || lower.includes('diferente')) return 'https://cdn.magicpatterns.com/uploads/f6PdBQJuuvgFKAyM5AdiMp/Screenshot_2026-04-26_at_11.47.18_PM.png';
  if (lower.includes('combo') || lower.includes('promo')) return '🎉';
  if (lower.includes('entrada') || lower.includes('appetizer')) return '🍽️';
  if (lower.includes('taco')) return '🌮';
  if (lower.includes('sopa') || lower.includes('soup')) return '🍲';
  if (lower.includes('pasta')) return '🍝';
  if (lower.includes('sandwich') || lower.includes('sándwich')) return '🥪';
  if (lower.includes('desayuno') || lower.includes('breakfast')) return '🍳';
  if (lower.includes('niño') || lower.includes('kid') || lower.includes('pequeño')) return '👶';
  if (lower.includes('extra') || lower.includes('adicional')) return '✨';
  if (lower.includes('especial') || lower.includes('encargo')) return 'https://cdn.magicpatterns.com/uploads/p9c7FqKqxJzUz1k1HX2mEK/Screenshot_2026-04-26_at_11.48.15_PM.png';
  if (lower.includes('café') || lower.includes('coffee')) return '☕';
  if (lower.includes('cerveza') || lower.includes('beer')) return '🍺';
  if (lower.includes('vino') || lower.includes('wine')) return '🍷';
  if (lower.includes('cóctel') || lower.includes('cocktail')) return '🍹';
  return '🍽️';
}
// Priority sort: pizza-related categories first, then by DB sort_order
function getCategorySortPriority(name: string): number {
  const lower = name.toLowerCase().trim();
  if (lower.includes('pizza')) return 0;
  if (lower.includes('hamburguesa') || lower.includes('burger')) return 1;
  if (lower.includes('combo')) return 2;
  if (lower.includes('especial')) return 3;
  if (lower.includes('entrada')) return 4;
  if (lower.includes('ensalada')) return 5;
  if (lower.includes('postre')) return 80;
  if (lower.includes('bebida') || lower.includes('refresco')) return 90;
  if (lower.includes('extra') || lower.includes('adicional')) return 95;
  return 50;
}
export function RestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    t,
    lang
  } = useLanguage();
  const {
    restaurant,
    settings,
    loading: restaurantLoading,
    error
  } = useRestaurantBySlug(slug);
  const {
    categories,
    items,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  const {
    translatedItems,
    translatedCategories
  } = useTranslatedMenu(items, categories, lang);
  const {
    zones
  } = useMarketplaceData();
  const {
    addItem
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const {
    openOrder,
    shouldAlert,
    dismiss: dismissOpenOrder
  } = useOpenOrderCheck(restaurant?.id);
  const [showOpenOrderAlert, setShowOpenOrderAlert] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{
    item: MenuItem;
    quantity: number;
    options: CartItemOption[];
    notes: string;
  } | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeParent, setActiveParent] = useState<string>('comida');
  const [activeSubcategory, setActiveSubcategory] = useState<string>('');
  const menuSectionRef = useRef<HTMLDivElement>(null);
  // ─── Slug flags (MUST be before any code that references them) ─────────
  const isCongeladosSlug = slug === 'heladeria-congelados';
  const isNatuCafeSlug = slug?.includes('natucafe') ?? false;
  useEffect(() => {
    if (restaurant?.id) {
      monitorRestaurant(restaurant.id);
    }
  }, [restaurant?.id, monitorRestaurant]);
  // ─── Congelados category order ─────────────────────────────────────────
  const CONGELADOS_CATEGORY_ORDER = ['favoritos', 'bebidas frías & milkshakes', 'copas & postres helados', 'antojos dulces', 'refrescantes & granizados', 'opciones frescas', 'salados', 'combos & promos'];
  const getCongeladosCategoryRank = (name?: string | null): number => {
    const normalized = String(name ?? '').trim().toLowerCase();
    const idx = CONGELADOS_CATEGORY_ORDER.findIndex(item => normalized === item);
    return idx === -1 ? 999 : idx;
  };
  // ─── Map Categories to Parents ─────────────────────────────────────────
  const categoriesWithItems = (categories ?? []).filter(Boolean).filter(cat => (items ?? []).some(item => item?.category_id === cat.id));
  // Determine if this restaurant uses Kabú's parent grouping
  const useKabuGrouping = restaurant?.slug === 'kabu';
  const parentGroups = useKabuGrouping ?
  // Kabú: use the hardcoded parent category system
  KABU_PARENT_CATEGORIES.map(parent => {
    const subs = categoriesWithItems.filter(cat => getKabuParentKey(cat.name) === parent.key);
    const itemCount = (items ?? []).filter(item => subs.some(sub => sub.id === item?.category_id)).length;
    return {
      ...parent,
      subcategories: subs,
      itemCount
    };
  }).filter(p => p.itemCount > 0) :
  // Generic: each DB category becomes its own "parent" tab (flat)
  [...categoriesWithItems].sort((a, b) => {
    if (isCongeladosSlug) {
      // Congelados: use strategic category order, then DB sort_order
      const rankDiff = getCongeladosCategoryRank(a?.name) - getCongeladosCategoryRank(b?.name);
      if (rankDiff !== 0) return rankDiff;
      return (a?.sort_order ?? 0) - (b?.sort_order ?? 0);
    }
    // Other restaurants: keyword priority, then DB sort_order
    const prioDiff = getCategorySortPriority(a?.name ?? '') - getCategorySortPriority(b?.name ?? '');
    if (prioDiff !== 0) return prioDiff;
    return (a?.sort_order ?? 0) - (b?.sort_order ?? 0);
  }).map(cat => ({
    key: cat.id,
    labelKey: cat.name ?? '',
    icon: getCategoryIcon(cat.name ?? ''),
    subcategories: [cat],
    itemCount: (items ?? []).filter(item => item?.category_id === cat.id).length
  })).filter(p => p.itemCount > 0);
  useEffect(() => {
    if (parentGroups.length > 0 && !parentGroups.find(p => p.key === activeParent)) {
      const firstParent = parentGroups[0];
      setActiveParent(firstParent.key);
      if (firstParent.subcategories.length > 0) {
        setActiveSubcategory(firstParent.subcategories[0].id);
      }
    } else if (parentGroups.length > 0) {
      const currentParent = parentGroups.find(p => p.key === activeParent);
      if (currentParent && currentParent.subcategories.length > 0 && !activeSubcategory) {
        setActiveSubcategory(currentParent.subcategories[0].id);
      }
    }
  }, [parentGroups, activeParent, activeSubcategory]);
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    if (isKitchenPaused) {
      alert('La cocina está pausada en este momento. Vuelve en 10 minutos.');
      return;
    }
    // Show open order alert on first add attempt
    if (shouldAlert) {
      console.log('[OpenOrderCheck] Intercepting add-to-cart — showing alert');
      setPendingAdd({
        item,
        quantity,
        options,
        notes
      });
      setShowOpenOrderAlert(true);
      return;
    }
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options,
      station_code: item.station_code || null
    }, restaurant.name);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    }
  };
  const handleDismissOpenOrder = () => {
    dismissOpenOrder();
    setShowOpenOrderAlert(false);
    // Process the pending add
    if (pendingAdd && restaurant) {
      const {
        item,
        quantity,
        options,
        notes
      } = pendingAdd;
      const success = addItem({
        menu_item_id: item.id,
        restaurant_id: restaurant.id,
        item_name_snapshot: item.name,
        unit_price_snapshot: item.price,
        quantity,
        notes,
        image_url: item.image_url,
        selected_options: options,
        station_code: item.station_code || null
      }, restaurant.name);
      if (!success) {
        alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
      }
      setPendingAdd(null);
    }
  };
  const handleCancelOpenOrder = () => {
    setShowOpenOrderAlert(false);
    setPendingAdd(null);
  };
  const handleSelectParent = (parentKey: string) => {
    setActiveParent(parentKey);
    const parent = parentGroups.find(p => p.key === parentKey);
    if (parent && parent.subcategories.length > 0) {
      setActiveSubcategory(parent.subcategories[0].id);
    }
    scrollToMenu();
  };
  const handleSelectSubcategory = (categoryId: string) => {
    setActiveSubcategory(categoryId);
  };
  const scrollToMenu = () => {
    if (menuSectionRef.current) {
      const y = menuSectionRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // ─── Loading ───
  if (restaurantLoading) {
    return <div className={`min-h-screen w-full flex flex-col ${isNatuCafeSlug ? 'bg-[#0F0D0B]' : isCongeladosSlug ? 'bg-[#FBF5FF]' : 'bg-[#FBF7F2]'}`} data-id="element-5130">
        <MarketplaceHeader data-id="element-5131" />
        <div className={`h-48 sm:h-64 w-full ${isNatuCafeSlug ? 'bg-[#1A1614]' : isCongeladosSlug ? 'bg-[#E8DDF0]' : 'bg-[#F5EDE3]'} animate-pulse`} data-id="element-5132" />
        <div className="max-w-3xl mx-auto px-4 w-full mt-10 space-y-4" data-id="element-5133">
          <div className={`h-8 w-48 ${isNatuCafeSlug ? 'bg-[#2A2420]' : isCongeladosSlug ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'} rounded-lg animate-pulse`} data-id="element-5134" />
          <div className={`h-4 w-72 ${isNatuCafeSlug ? 'bg-[#2A2420]' : isCongeladosSlug ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'} rounded animate-pulse`} data-id="element-5135" />
          <div className="space-y-3 mt-8" data-id="element-5136">
            {[1, 2, 3, 4].map(i => <div key={i} className={`h-20 rounded-xl ${isNatuCafeSlug ? 'bg-[#2A2420]' : isCongeladosSlug ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'} animate-pulse`} data-id="element-5137" />)}
          </div>
        </div>
      </div>;
  }
  // ─── Error ───
  if (error || !restaurant) {
    return <div className={`min-h-screen w-full flex flex-col ${isNatuCafeSlug ? 'bg-[#0F0D0B]' : isCongeladosSlug ? 'bg-[#FBF5FF]' : 'bg-[#FBF7F2]'}`} data-id="element-5138">
        <MarketplaceHeader data-id="element-5139" />
        <div className="flex-1 flex items-center justify-center" data-id="element-5140">
          <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe o no está disponible en este momento." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-5141" />
        </div>
        <Footer data-id="element-5142" />
      </div>;
  }
  const zoneName = zones.find(z => z.id === restaurant.zone_id)?.name;
  const highlightedItems = items.slice(0, 3);
  const isEmilius = restaurant.slug === 'emilius-pizza';
  const isKabu = restaurant.slug === 'kabu';
  const isCongelados = restaurant.slug === 'heladeria-congelados';
  const isNatuCafe = restaurant.slug?.includes('natucafe') ?? false;
  const primaryColor = isEmilius ? '#D42B2B' : isKabu ? '#D4A853' : isCongelados ? '#7B2D8E' : isNatuCafe ? '#C6A969' : restaurant.primary_color || '#8B5E3C';
  const activeParentData = parentGroups.find(p => p.key === activeParent);
  const bgColor = isNatuCafe ? 'bg-[#0F0D0B]' : isCongelados ? 'bg-[#FBF5FF]' : 'bg-[#FBF7F2]';
  const borderColor = isNatuCafe ? 'border-[#2A2420]' : isCongelados ? 'border-[#E8DDF0]' : 'border-[#E8DDD0]';
  const titleColor = isNatuCafe ? 'text-[#F5F0EB]' : isCongelados ? 'text-[#7B2D8E]' : 'text-[#3D2B1F]';
  const subtitleColor = isNatuCafe ? 'text-[#C6A969]' : isCongelados ? 'text-[#5D3A1A]' : 'text-[#8B7355]';
  const mutedTextColor = isNatuCafe ? 'text-[#8B7B6B]' : isCongelados ? 'text-[#7B2D8E]/70' : 'text-[#8B7355]/70';
  const hoverBgColor = isNatuCafe ? 'hover:bg-[#C6A969]/5' : isCongelados ? 'hover:bg-[#7B2D8E]/5' : 'hover:bg-[#8B5E3C]/5';
  return <div className={`min-h-screen w-full flex flex-col ${bgColor} pb-24`} data-id="element-5143">
      <MarketplaceHeader data-id="element-5144" />

      <main className="flex-1 relative" data-id="element-5145">
        <RestaurantHeader restaurant={restaurant} settings={settings} zoneName={zoneName} data-id="element-5146" />

        {/* Decorative coffee plant elements - NatuCafé only */}
        {isNatuCafe && <>
            {/* Top right coffee branch */}
            <div className="absolute top-20 right-0 w-64 h-64 pointer-events-none opacity-[0.08] z-0" data-id="element-5147">
              <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5148">
                {/* Coffee branch with leaves and berries */}
                <path d="M20 180 Q 60 140, 80 100 T 120 40" stroke="#2A2420" strokeWidth="3" fill="none" data-id="element-5149" />
                {/* Leaves */}
                <ellipse cx="50" cy="160" rx="18" ry="28" fill="#2A2420" opacity="0.9" transform="rotate(-25 50 160)" data-id="element-5150" />
                <ellipse cx="70" cy="130" rx="20" ry="30" fill="#C6A969" opacity="0.3" transform="rotate(15 70 130)" data-id="element-5151" />
                <ellipse cx="90" cy="95" rx="18" ry="28" fill="#2A2420" opacity="0.9" transform="rotate(-30 90 95)" data-id="element-5152" />
                <ellipse cx="105" cy="70" rx="16" ry="24" fill="#2A2420" opacity="0.8" transform="rotate(20 105 70)" data-id="element-5153" />
                <ellipse cx="115" cy="50" rx="14" ry="22" fill="#C6A969" opacity="0.3" transform="rotate(-15 115 50)" data-id="element-5154" />
                {/* Coffee berries */}
                <circle cx="65" cy="145" r="5" fill="#D4883A" opacity="0.4" data-id="element-5155" />
                <circle cx="68" cy="148" r="4" fill="#D4883A" opacity="0.4" data-id="element-5156" />
                <circle cx="95" cy="110" r="5" fill="#D4883A" opacity="0.4" data-id="element-5157" />
                <circle cx="98" cy="107" r="4" fill="#D4883A" opacity="0.4" data-id="element-5158" />
              </svg>
            </div>

            {/* Bottom left coffee branch */}
            <div className="absolute bottom-32 left-0 w-56 h-56 pointer-events-none opacity-[0.06] z-0" data-id="element-5159">
              <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5160">
                <path d="M180 20 Q 140 60, 120 100 T 80 160" stroke="#2A2420" strokeWidth="3" fill="none" data-id="element-5161" />
                <ellipse cx="150" cy="40" rx="16" ry="26" fill="#2A2420" opacity="0.9" transform="rotate(25 150 40)" data-id="element-5162" />
                <ellipse cx="130" cy="70" rx="18" ry="28" fill="#C6A969" opacity="0.3" transform="rotate(-15 130 70)" data-id="element-5163" />
                <ellipse cx="110" cy="105" rx="17" ry="26" fill="#2A2420" opacity="0.9" transform="rotate(30 110 105)" data-id="element-5164" />
                <ellipse cx="95" cy="130" rx="15" ry="23" fill="#2A2420" opacity="0.8" transform="rotate(-20 95 130)" data-id="element-5165" />
                <circle cx="135" cy="55" r="5" fill="#D4883A" opacity="0.4" data-id="element-5166" />
                <circle cx="115" cy="90" r="4" fill="#D4883A" opacity="0.4" data-id="element-5167" />
                <circle cx="100" cy="118" r="5" fill="#D4883A" opacity="0.4" data-id="element-5168" />
              </svg>
            </div>
          </>}

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 relative z-10" data-id="element-5169">
          {/* ─── Highlighted Items — editorial feature cards ─── */}
          {highlightedItems.length > 0 && <section className="mb-10 sm:mb-14" data-id="element-5170">
              <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${mutedTextColor} mb-4`} data-id="element-5171">
                {t('recommended')}
              </p>
              <div className="space-y-1" data-id="element-5172">
                {highlightedItems.map(item => <div key={item.id} onClick={() => handleProductClick(item)} className={`group flex items-center gap-4 py-3.5 cursor-pointer border-b ${borderColor} last:border-b-0 ${hoverBgColor} transition-colors rounded-lg`} data-id="element-5173">
                    <img src={item.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300&h=300&fit=crop&q=60'} alt={item.name} className="w-14 h-14 rounded-xl object-cover shrink-0" loading="lazy" data-id="element-5174" />
                    <div className="flex-1 min-w-0" data-id="element-5175">
                      <h3 className={`font-semibold ${titleColor} text-[15px] leading-tight line-clamp-1 group-hover:opacity-80 transition-opacity`} data-id="element-5176">
                        {translatedItems.find(ti => ti.id === item.id)?.name || item.name}
                      </h3>
                      {item.description && <p className={`text-[12px] ${subtitleColor} line-clamp-1 mt-0.5`} data-id="element-5177">
                          {translatedItems.find(ti => ti.id === item.id)?.description || item.description}
                        </p>}
                    </div>
                    <span className="font-semibold text-sm shrink-0" style={{
                color: primaryColor
              }} data-id="element-5178">
                      {formatCurrency(item.price)}
                    </span>
                  </div>)}
              </div>
            </section>}

          {/* ─── Parent Category Selector — minimal inline pills ─── */}
          <section className="mb-10 sm:mb-14" data-id="element-5179">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${mutedTextColor} mb-5`} data-id="element-5180">
              {t('explore')}
            </p>
            {menuLoading ? <div className="flex gap-3" data-id="element-5181">
                {[1, 2, 3, 4].map(i => <div key={i} className={`h-16 w-28 rounded-2xl ${isNatuCafe ? 'bg-[#2A2420]' : isCongelados ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'} animate-pulse`} data-id="element-5182" />)}
              </div> : parentGroups.length > 0 ? <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-1" data-id="element-5183">
                {parentGroups.map(parent => {
              const isActive = activeParent === parent.key;
              return <button key={parent.key} onClick={() => handleSelectParent(parent.key)} className="shrink-0 flex flex-col items-center gap-2 px-6 py-4 rounded-2xl transition-all active:scale-95" style={{
                backgroundColor: isActive ? `${primaryColor}15` : isNatuCafe ? '#1A1614' : '#FFFFFF',
                borderWidth: 1,
                borderColor: isActive ? `${primaryColor}40` : isNatuCafe ? '#2A2420' : isCongelados ? '#E8DDF0' : '#E8DDD0'
              }} data-id="element-5184">
                      {parent.icon.startsWith('http') ? <img src={parent.icon} alt="" className="w-8 h-8 object-cover rounded-full" data-id="element-5185" /> : <span className="text-2xl" data-id="element-5186">{parent.icon}</span>}
                      <span className={`text-xs font-semibold ${isActive ? titleColor : mutedTextColor}`} data-id="element-5187">
                        {t(parent.labelKey)}
                      </span>
                    </button>;
            })}
              </div> : <p className={`${mutedTextColor} text-sm`} data-id="element-5188">
                No hay categorías disponibles.
              </p>}
          </section>

          {/* ─── Subtle divider with scroll hint ─── */}
          <div className="flex items-center gap-3 mb-8" data-id="element-5189">
            <div className={`flex-1 h-px ${isNatuCafe ? 'bg-[#2A2420]' : isCongelados ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'}`} data-id="element-5190" />
            <button onClick={scrollToMenu} className={`flex items-center gap-1.5 ${isNatuCafe ? 'text-[#C6A969]/50 hover:text-[#C6A969]' : isCongelados ? 'text-[#7B2D8E]/50 hover:text-[#7B2D8E]' : 'text-[#8B7355]/50 hover:text-[#8B7355]'} transition-colors`} data-id="element-5191">
              <span className="text-[11px] font-medium uppercase tracking-widest" data-id="element-5192">
                {t('menu')}
              </span>
              <ArrowDown className="w-3 h-3" data-id="element-5193" />
            </button>
            <div className={`flex-1 h-px ${isNatuCafe ? 'bg-[#2A2420]' : isCongelados ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'}`} data-id="element-5194" />
          </div>

          {/* ═══ FULL MENU SECTION ═══ */}
          <div ref={menuSectionRef} data-id="element-5195">
            {parentGroups.length > 0 && activeParentData && <>
                <MenuParentTabs parents={parentGroups.map(p => ({
              key: p.key,
              label: t(p.labelKey),
              icon: p.icon
            }))} activeParent={activeParent} onSelectParent={handleSelectParent} subcategories={activeParentData.subcategories.map(c => ({
              ...c,
              name: translateCategory(c.name, lang)
            }))} activeSubcategory={activeSubcategory} onSelectSubcategory={handleSelectSubcategory} primaryColor={primaryColor} bgClassName={isNatuCafe ? 'bg-[#0F0D0B]/95 border-[#2A2420]' : isCongelados ? 'bg-[#FBF5FF]/95 border-[#E8DDF0]' : undefined} isDark={isNatuCafe} data-id="element-5196" />

                {/* Menu items — filtered by active subcategory */}
                <div className="mt-8" data-id="element-5197">
                  <AnimatePresence mode="wait" data-id="element-5198">
                    {(() => {
                  const activeCategory = activeParentData.subcategories.find(c => c.id === activeSubcategory);
                  if (!activeCategory) return null;
                  const filteredItems = items.filter(item => item.category_id === activeCategory.id);
                  if (filteredItems.length === 0) return null;
                  return <motion.div key={activeSubcategory} initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.15
                  }} data-id="element-5199">
                          <div className="flex items-center gap-3 mb-3 sm:mb-4" data-id="element-5200">
                            <div className="w-1 h-5 rounded-full" style={{
                        backgroundColor: primaryColor
                      }} data-id="element-5201" />
                            <h3 className={`text-base sm:text-lg font-bold ${titleColor} tracking-tight`} data-id="element-5202">
                              {translateCategory(activeCategory.name, lang)}
                            </h3>
                            <span className={`text-xs ${isNatuCafe ? 'text-[#C6A969]/50' : isCongelados ? 'text-[#7B2D8E]/50' : 'text-[#8B7355]/50'} font-medium`} data-id="element-5203">
                              {filteredItems.length}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4" data-id="element-5204">
                            {filteredItems.map(item => {
                        const ti = translatedItems.find(t => t.id === item.id);
                        return <MenuItemCard key={item.id} item={item} onClick={() => handleProductClick(item)} primaryColor={primaryColor} displayName={ti?.name} displayDescription={ti?.description} theme={isNatuCafe ? 'natucafe' : 'capricho'} data-id="element-5205" />;
                      })}
                          </div>
                        </motion.div>;
                })()}
                  </AnimatePresence>
                </div>
              </>}

            {/* Fallback */}
            {categories.length === 0 && items.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4" data-id="element-5206">
                {items.map(item => {
              const ti = translatedItems.find(t => t.id === item.id);
              return <MenuItemCard key={item.id} item={item} onClick={() => handleProductClick(item)} primaryColor={primaryColor} displayName={ti?.name} displayDescription={ti?.description} theme={isNatuCafe ? 'natucafe' : 'capricho'} data-id="element-5207" />;
            })}
              </div>}
          </div>
        </div>
      </main>

      <FloatingCartButton primaryColor={primaryColor} data-id="element-5208" />

      {selectedProduct && <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={primaryColor} data-id="element-5209" />}

      <Footer data-id="element-5210" />

      {/* Open Order Alert */}
      {showOpenOrderAlert && openOrder && <OpenOrderAlert order={openOrder} onDismiss={handleDismissOpenOrder} onViewTracking={handleCancelOpenOrder} data-id="element-5211" />}
    </div>;
}